import React, { useMemo, useState, useEffect } from 'react';
import { set, get, getOr, has, size, omit, find, some } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Button, Icon, SelectPicker, FormControl, ErrorMessage } from 'rsuite';
import { parseInt } from '../../../helpers/utils';
import {
  composeLimitRulesOptionKey,
  limitRulesMapToOptions
} from '../../../helpers/limitRules';
import { SelectOptionsType } from '../../../interfaces';
import { LimitRulesItemType } from '../../../interfaces/reservation';
import { FormGroup } from '../../widgets/FormElements';
import LimitRulesModal from '../LimitRulesModal';
import OrderItemName from '../OrderItemName';
import {
  StyledFormGroup,
  ItemsControlLabel,
  CustomFormControl,
  LimitRulesSelectPicker
} from './elements';

const EmptyItem = {
  itemId: null,
  name: '',
  photo: '',
  healthLevel: null,
  category: null,
  limitRules: null,
  position: 0,
  isVisible: true,
  enabled: true
};
const ItemExistsError = 'Item already exists';

const composeItemOption = item => {
  const { itemId: value, name: label } = item;
  const meta = {
    photo: get('photo', item),
    healthLevel: get('healthLevel', item),
    inventorySnapshot: get('inventorySnapshot', item)
  };
  return { value: parseInt(value), label, meta };
};

const CategoryItemsFormGroup = ({
  data,
  category,
  onCheck,
  onChange,
  limitRulesMap,
  itemOptions
}) => {
  const [newItem, setNewItem] = useState(EmptyItem);
  const [showLimitRules, setShowLimitRules] = useState(false);
  const [newItemError, setNewItemError] = useState(false);
  const [fieldError, setFieldError] = useState({});
  const disabledAddItem = !newItem.itemId || !!newItemError;

  const limitRulesOptions = useMemo(
    () => limitRulesMapToOptions(limitRulesMap),
    [limitRulesMap]
  );

  // New Item Handlers
  const handleSelectItem = itemId => {
    const { label, meta } = find({ value: itemId }, itemOptions);
    const { photo, healthLevel, inventorySnapshot } = meta;
    const itemExists = some({ itemId }, data);

    setNewItem({
      ...newItem,
      category,
      itemId: itemId.toString(),
      name: label,
      photo,
      healthLevel,
      inventorySnapshot
    });
    setNewItemError(itemExists ? ItemExistsError : '');
  };

  const handleSelectLimitRule = value => {
    const isCreateNew = value === 0;
    if (isCreateNew) {
      setShowLimitRules(true);
      setNewItem(set('position', 0, newItem));
    } else setNewItem(set('limitRules', limitRulesMap[value], newItem));
  };

  // Edit Item Handlers
  const handleEditSelectItem = i => itemId => {
    const item = get(i, data);
    const itemExists = size(find({ itemId }, data)) > 1;

    if (itemExists) setFieldError(set(i, ItemExistsError, fieldError));
    else setFieldError(omit(i, fieldError));

    const { label, meta } = find({ value: itemId }, itemOptions);
    const { photo, healthLevel, inventorySnapshot } = meta;
    const selectedItem = {
      ...item,
      itemId,
      name: label,
      photo,
      healthLevel,
      inventorySnapshot
    };
    onChange(set(i, selectedItem, data));
  };

  const handleEditSelectLimitRules = i => value => {
    const isCreateNew = value === 0;
    if (isCreateNew) {
      setShowLimitRules(true);
      setNewItem(set('position', i + 1, newItem));
    } else onChange(set(`${i}.limitRules`, limitRulesMap[value], data));
  };

  const handleHideLimitRules = () => {
    setShowLimitRules(false);
  };

  const handleSaveLimitRules = value => {
    setShowLimitRules(false);
    if (newItem.position > 0) {
      onChange(set(`${newItem.position - 1}.limitRules`, value, data));
      return;
    }

    if (newItem.name) {
      setNewItem(EmptyItem);
      onChange([...data, set('limitRules', value, newItem)]);
    } else {
      setNewItem(set('limitRules', value, newItem));
    }
  };

  const handleDeleteItem = i => () => {
    const clonedData = [...data];
    clonedData.splice(i, 1);
    onChange(clonedData);
    if (has(i, fieldError)) setFieldError(omit(i, fieldError));
  };

  const handleSaveItem = () => {
    onChange([...data, newItem]);
    setNewItem(EmptyItem);
  };

  useEffect(
    () => {
      onCheck(fieldError);
    },
    [fieldError]
  );

  useEffect(
    () => {
      const errorMsg = 'Must have at least 1 item';
      const hasItem = size(data);
      setNewItemError(hasItem ? '' : errorMsg);
      onCheck(hasItem ? {} : { 1: errorMsg });
    },
    [data]
  );

  const handleToggleItem = i => () => {
    const item = get(i, data);
    const enabled = getOr(true, 'enabled', item);

    onChange(set(i, { ...item, enabled: !enabled }, data));
  };

  const renderMenuItem = (label, item) => (
    <OrderItemName
      itemName={label}
      inventorySnapshot={get('meta.inventorySnapshot', item)}
    />
  );

  const renderItemValue = (_, item) =>
    item ? (
      <OrderItemName
        itemName={get('label', item)}
        inventorySnapshot={get('meta.inventorySnapshot', item)}
      />
    ) : null;

  return (
    <FormGroup style={{ marginTop: 10 }}>
      <ItemsControlLabel>
        Items (
        {
          data.filter(
            ({ isVisible = 1 }) =>
              isVisible === true || parseInt(isVisible) === 1
          ).length
        }
        ) *
      </ItemsControlLabel>
      {data.map((each, i) => {
        const { isVisible = 1, enabled = true } = each;
        const itemId = parseInt(each.itemId);
        return (
          <StyledFormGroup
            key={`item-${each.categoryId}-${itemId}`}
            className={
              parseInt(isVisible) === 0 || isVisible === false ? 'hide' : ''
            }
          >
            <FormControl
              cleanable={false}
              accepter={SelectPicker}
              placeholder="Select Item"
              value={itemId}
              onChange={handleEditSelectItem(i)}
              errorMessage={get(i, fieldError)}
              errorPlacement="leftEnd"
              data={[composeItemOption(each), ...itemOptions]}
              width={418}
              style={{ width: 245, marginRight: 8 }}
              renderMenuItem={renderMenuItem}
              renderValue={renderItemValue}
            />
            <LimitRulesSelectPicker
              placeholder="Select Item Limit Rule"
              name={`${i}.limitRules`}
              value={composeLimitRulesOptionKey(each.limitRules)}
              style={{ width: 306, marginRight: 8 }}
              onChange={handleEditSelectLimitRules(i)}
              data={limitRulesOptions}
            />
            <Button onClick={handleDeleteItem(i)} style={{ marginRight: 8 }}>
              <Icon icon="trash" color="red" style={{ color: 'red' }} />
            </Button>
            <Button onClick={handleToggleItem(i)}>
              <Icon
                icon={enabled ? 'eye' : 'eye-slash'}
                style={{ color: enabled ? 'green' : 'gray' }}
              />
            </Button>
          </StyledFormGroup>
        );
      })}
      <FormGroup>
        <CustomFormControl>
          <SelectPicker
            placeholder="Select Item"
            onChange={handleSelectItem}
            style={{ width: 245, marginRight: 8 }}
            value={parseInt(newItem.itemId)}
            data={itemOptions}
            cleanable={false}
            renderMenuItem={renderMenuItem}
            renderValue={renderItemValue}
          />
          {newItemError && <ErrorMessage show>{newItemError}</ErrorMessage>}
        </CustomFormControl>
        <LimitRulesSelectPicker
          cleanable={false}
          placeholder="Select Item Limit Rule"
          value={composeLimitRulesOptionKey(newItem.limitRules)}
          style={{ width: 258, marginRight: 8 }}
          onChange={handleSelectLimitRule}
          disabled={!newItem.name}
          data={limitRulesOptions}
        />
        <Button disabled={disabledAddItem} onClick={handleSaveItem}>
          <Icon
            icon="plus-circle"
            style={!disabledAddItem ? { color: 'green' } : {}}
          />
        </Button>
      </FormGroup>
      <LimitRulesModal
        show={showLimitRules}
        onSave={handleSaveLimitRules}
        onCancel={handleHideLimitRules}
      />
    </FormGroup>
  );
};

const ItemType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  limitRules: PropTypes.arrayOf(LimitRulesItemType)
});

CategoryItemsFormGroup.propTypes = {
  data: PropTypes.arrayOf(ItemType),
  category: PropTypes.string.isRequired,
  onCheck: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  limitRulesMap: PropTypes.shape({}),
  itemOptions: SelectOptionsType.isRequired
};

CategoryItemsFormGroup.defaultProps = {
  limitRulesMap: {},
  data: []
};

export default CategoryItemsFormGroup;
